import WarehouseAdminProductCategoryData, {
    IWarehouseAdminProductCategoryData
} from "@/http/data/warehouse-admin-product-category-data";
import WarehouseAdminProductSupplierData, {
    IWarehouseAdminProductSupplierData
} from "@/http/data/warehouse-admin-product-supplier-data";
import WarehouseAdminProductBuildingSpecificationData, {
    IWarehouseAdminProductBuildingSpecificationData
} from "@/http/data/warehouse-admin-product-building-specification-data";
import WarehouseAdminProductLocationData, {
    IWarehouseAdminProductLocationData
} from "@/http/data/warehouse-admin-product-location-data";

export interface IWarehouseAdminProductDetailData {
    id: number;
    name: string;
    description: string|null;
    warehouse: number;
    rack: string|null;
    layer: string|null;
    place: string|null;
    retour: boolean;
    category_id: number;
    category: IWarehouseAdminProductCategoryData|null;
    type_id: number;
    target_price: number|null;
    temp_target_price: number|null;
    active: boolean;
    charge: boolean;
    min_stock: number|null;
    base_stock: number|null;
    restock_default: number;
    count: number;
    created: string|null;
    updated: string|null;
    image: string|null;
    thumb: string|null;
    image_timestamp: string|null;
    thumbnailUrl: string|null;
    imageUrl: string|null;
    units: number|null;
    height: number|null;
    width: number|null;
    depth: number|null;
    weight: number|null;
    inventory_check: boolean;
}

export interface IWarehouseAdminProductData {
    product: IWarehouseAdminProductDetailData;
    suppliers?: IWarehouseAdminProductSupplierData[];
    building_specifications?: IWarehouseAdminProductBuildingSpecificationData[];
    locations?: IWarehouseAdminProductLocationData[];
}

export default class WarehouseAdminProductData {
    public id: number;
    public name: string;
    public description: string|null;
    public warehouse: number;
    public rack: string|null;
    public layer: string|null;
    public place: string|null;
    public retour: boolean;
    public category_id: number;
    public category: WarehouseAdminProductCategoryData|null;
    public type_id: number;
    public target_price: number|null;
    public temp_target_price: number|null;
    public active: boolean;
    public charge: boolean;
    public min_stock: number|null;
    public base_stock: number|null;
    public restock_default: number;
    public count: number;
    public created: string|null;
    public updated: string|null;
    public image: string|null;
    public thumb: string|null;
    public image_timestamp: string|null;
    public thumbnailUrl: string|null;
    public imageUrl: string|null;
    public units: number|null;
    public height: number|null;
    public width: number|null;
    public depth: number|null;
    public weight: number|null;
    public inventory_check: boolean;
    public suppliers: WarehouseAdminProductSupplierData[];
    public building_specifications: WarehouseAdminProductBuildingSpecificationData[];
    public locations: WarehouseAdminProductLocationData[];

    constructor(data: IWarehouseAdminProductData) {
        const product = data.product;

        this.id = Number(product.id);
        this.name = product.name;
        this.description = product.description ?? null;
        this.warehouse = Number(product.warehouse);
        this.rack = product.rack ?? null;
        this.layer = product.layer ?? null;
        this.place = product.place ?? null;
        this.retour = Boolean(product.retour);
        this.category_id = Number(product.category_id);
        this.category = product.category
            ? new WarehouseAdminProductCategoryData(product.category)
            : null;
        this.type_id = Number(product.type_id);
        this.target_price = product.target_price === null
            ? null
            : Number(product.target_price);
        this.temp_target_price = product.temp_target_price === null
            ? null
            : Number(product.temp_target_price);
        this.active = Boolean(product.active);
        this.charge = Boolean(product.charge);
        this.min_stock = product.min_stock === null
            ? null
            : Number(product.min_stock);
        this.base_stock = product.base_stock === null
            ? null
            : Number(product.base_stock);
        this.restock_default = Number(product.restock_default);
        this.count = Number(product.count);
        this.created = product.created ?? null;
        this.updated = product.updated ?? null;
        this.image = product.image ?? null;
        this.thumb = product.thumb ?? null;
        this.image_timestamp = product.image_timestamp ?? null;
        this.thumbnailUrl = product.thumbnailUrl ?? null;
        this.imageUrl = product.imageUrl ?? null;
        this.units = product.units === null
            ? null
            : Number(product.units);
        this.height = product.height === null
            ? null
            : Number(product.height);
        this.width = product.width === null
            ? null
            : Number(product.width);
        this.depth = product.depth === null
            ? null
            : Number(product.depth);
        this.weight = product.weight === null
            ? null
            : Number(product.weight);
        this.inventory_check = Boolean(product.inventory_check);
        this.suppliers = (data.suppliers ?? []).map(
            supplier => new WarehouseAdminProductSupplierData(supplier)
        );
        this.building_specifications = (data.building_specifications ?? []).map(
            specification => new WarehouseAdminProductBuildingSpecificationData(specification)
        );
        this.locations = (data.locations ?? []).map(
            location => new WarehouseAdminProductLocationData(location)
        );
    }
}